import type * as React from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { DismissableLayer } from '@radix-ui/react-dismissable-layer'
import {
  arrow as arrowMiddleware,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/dom'
import type { Placement } from '@floating-ui/dom'

import usePortalContainer from '../../helpers/usePortalContainer'
import { TooltipShortcut } from './TooltipShortcut'

import styles from './TooltipGroup.module.css'

type TooltipSide = 'top' | 'right' | 'bottom' | 'left'
type TooltipAlign = 'start' | 'center' | 'end'

type TooltipGroupMember = {
  id: string
  element: HTMLElement
  content: React.ReactNode
  shortcut?: string
  side: TooltipSide
  align: TooltipAlign
  sideOffset: number
  arrow: boolean
}

type TooltipGroupContextValue = {
  /** Id applied to the shared bubble, for the active trigger's `aria-describedby`. */
  contentId: string
  activeId: string | null
  show: (member: TooltipGroupMember) => void
  scheduleHide: (id: string) => void
  /** Closes immediately, bypassing the hide grace — for a trigger that's unmounting. */
  forceHide: (id: string) => void
}

const TooltipGroupContext = createContext<TooltipGroupContextValue | undefined>(
  undefined,
)

function useTooltipGroup(): TooltipGroupContextValue | undefined {
  return useContext(TooltipGroupContext)
}

// Radix fires the outgoing member's blur/leave and the incoming member's
// focus/enter as two separate events, not one atomic switch. Deferring the
// hide briefly lets a same-group `show` cancel it, so the bubble jumps
// directly from one trigger's content to the next's instead of closing and
// reopening — the difference between one bubble traveling and two popping.
const HIDE_GRACE_MS = 100

const ARROW_SIZE = 8

// Matches --space-2xs, the gap a standalone (non-grouped) Tooltip gets from
// Tooltip.module.css's margin-bottom. Floating-ui's `offset` middleware has
// no equivalent by default, so without this the shared bubble sits flush
// against its trigger while every other tooltip in the app doesn't.
const GAP = 4

const PLACEMENTS: Record<TooltipSide, Record<TooltipAlign, Placement>> = {
  top: { start: 'top-start', center: 'top', end: 'top-end' },
  right: { start: 'right-start', center: 'right', end: 'right-end' },
  bottom: { start: 'bottom-start', center: 'bottom', end: 'bottom-end' },
  left: { start: 'left-start', center: 'left', end: 'left-end' },
}

// The side an arrow sits on is the opposite of the bubble's placement side —
// used to pin the arrow to the edge facing the trigger.
const STATIC_SIDE_BY_PLACEMENT: Record<Placement, TooltipSide> = {
  top: 'bottom',
  'top-start': 'bottom',
  'top-end': 'bottom',
  right: 'left',
  'right-start': 'left',
  'right-end': 'left',
  bottom: 'top',
  'bottom-start': 'top',
  'bottom-end': 'top',
  left: 'right',
  'left-start': 'right',
  'left-end': 'right',
}

function toPlacement(side: TooltipSide, align: TooltipAlign): Placement {
  return PLACEMENTS[side][align]
}

type TooltipGroupProps = {
  /**
   * `Tooltip`s rendered anywhere inside (including nested inside `IconButton`)
   * automatically become members: they share one tooltip bubble that stays
   * mounted and travels between triggers instead of each fading in/out on
   * its own.
   */
  children: React.ReactNode
  /**
   * How long, in ms, the group stays "hot" after the bubble closes. Hovering
   * a new member within this window skips its open delay.
   * @default 300
   */
  hotWindow?: number
  /**
   * Delay, in ms, before the very first (cold) open in this group. Overrides
   * each member's own `delay` for that one moment — members often tune
   * `delay` for being hovered in isolation (e.g. `IconButton`'s 800ms, so
   * dense icon-only controls feel less noisy), which isn't the right amount
   * of friction for a deliberate, curated cluster like a toolbar. Hot swaps
   * between members are unaffected either way — those are always instant.
   * @default 150
   */
  delay?: number
}

function TooltipGroup({
  children,
  hotWindow = 300,
  delay = 150,
}: TooltipGroupProps): React.JSX.Element {
  const contentId = useId()
  const container = usePortalContainer()

  type Active = { member: TooltipGroupMember; fresh: boolean } | null

  const [active, setActiveState] = useState<Active>(null)
  const [isHot, setIsHot] = useState(false)
  const isHotRef = useRef(isHot)
  isHotRef.current = isHot

  // The authoritative "what's active right now" check. React state (`active`
  // above) only reflects the last committed render, so a `setActive(current
  // => ...)` updater peeking at "current" can see a call's *own* just-applied
  // result if `show()` re-enters before that render lands (observed in
  // practice: the same trigger's pointerenter firing twice in a row makes the
  // second call's updater see the first call's result instead of the prior
  // state, flipping `fresh` to false and replaying the corner-flying bug).
  // Routing every check through this ref instead — updated in lockstep with
  // every `setActiveState` call, synchronously and unconditionally — removes
  // the reentrancy hazard entirely.
  const activeRef = useRef<Active>(null)

  const setActive = (next: Active): void => {
    activeRef.current = next
    setActiveState(next)
  }

  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const pendingIdRef = useRef<string | null>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const hotTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return (): void => {
      clearTimeout(openTimeoutRef.current)
      clearTimeout(hideTimeoutRef.current)
      clearTimeout(hotTimeoutRef.current)
    }
  }, [])

  // Passed to the bubble's DismissableLayer, which owns deciding *when* this
  // fires (Escape, or an outside interaction) — see TooltipGroupBubble.
  // Delegating to that primitive, the same one Popcorn's own Dialog uses
  // internally, is what makes Escape close only the tooltip while a dialog
  // is open behind it: DismissableLayer tracks a shared stack of open layers
  // and only the topmost one's Escape handler is attached at all, so the
  // dialog's own handler is simply inactive for as long as this one is
  // mounted. A plain `document` keydown listener can't get this right no
  // matter how it orders itself, since the dialog's own DismissableLayer
  // listens in the capture phase and already ran by the time a bubble-phase
  // listener added later would fire.
  const handleDismiss = (): void => {
    clearTimeout(openTimeoutRef.current)
    clearTimeout(hideTimeoutRef.current)
    pendingIdRef.current = null
    setActive(null)
    hotTimeoutRef.current = setTimeout(() => setIsHot(false), hotWindow)
  }

  const value = useMemo<TooltipGroupContextValue>(() => {
    const show = (member: TooltipGroupMember): void => {
      // Cancel any pending hide unconditionally, before deciding anything
      // else. A hide scheduled for this same trigger (a pointerleave/blur
      // that a pointerenter/focus is now reversing) must never fire later
      // and close what's active again — and coalescing leave+enter into one
      // swap for a *different* trigger relies on the same cancellation.
      clearTimeout(hideTimeoutRef.current)

      if (activeRef.current?.member.id === member.id) {
        // Same trigger already active — a duplicate pointerenter/focus for
        // one hover, or this trigger's own content changed while it's still
        // being hovered (e.g. a toggle button's label). Refresh the content
        // in place; position and fresh state are already correct.
        setActive({ member, fresh: activeRef.current.fresh })
        return
      }

      clearTimeout(hotTimeoutRef.current)
      clearTimeout(openTimeoutRef.current)
      pendingIdRef.current = null

      if (isHotRef.current) {
        // "Hot" only means the group skips the open delay — it doesn't mean
        // the bubble is still mounted. `HIDE_GRACE_MS` (100ms) is much
        // shorter than `hotWindow` (300ms), so there's a window where the
        // previous bubble already unmounted but the group is still hot. A
        // remount here has no valid position to transition *from*, so it
        // must be treated as fresh (snap into place, no transform-transition)
        // even though the group itself is hot — checking the *previous*
        // active value (not `isHot`) is what actually answers "is this a new
        // mount."
        setActive({ member, fresh: activeRef.current === null })
        return
      }

      pendingIdRef.current = member.id
      openTimeoutRef.current = setTimeout(() => {
        pendingIdRef.current = null
        setActive({ member, fresh: true })
        setIsHot(true)
      }, delay)
    }

    const scheduleHide = (id: string): void => {
      if (pendingIdRef.current === id) {
        clearTimeout(openTimeoutRef.current)
        pendingIdRef.current = null
        return
      }

      // A focused-and-hovered trigger fires both pointerleave and blur on
      // the way out; only the most recent hide attempt should ever be live.
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = setTimeout(() => {
        // A stale hide for a trigger that's no longer active must not cool
        // the group down while a different member is genuinely in use.
        if (activeRef.current?.member.id !== id) return
        setActive(null)
        hotTimeoutRef.current = setTimeout(() => setIsHot(false), hotWindow)
      }, HIDE_GRACE_MS)
    }

    const forceHide = (id: string): void => {
      if (pendingIdRef.current === id) {
        clearTimeout(openTimeoutRef.current)
        pendingIdRef.current = null
        return
      }

      if (activeRef.current?.member.id !== id) return

      // Bypasses the hide grace: called when the active trigger itself is
      // unmounting, so there's no element left to travel from or return to.
      clearTimeout(hideTimeoutRef.current)
      setActive(null)
      hotTimeoutRef.current = setTimeout(() => setIsHot(false), hotWindow)
    }

    return {
      contentId,
      activeId: active?.member.id ?? null,
      show,
      scheduleHide,
      forceHide,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, active?.member.id, hotWindow, delay])

  return (
    <TooltipGroupContext.Provider value={value}>
      {children}
      {active && (
        <TooltipGroupBubble
          id={contentId}
          member={active.member}
          container={container}
          fresh={active.fresh}
          onPointerEnter={(): void => value.show(active.member)}
          onPointerLeave={(): void => value.scheduleHide(active.member.id)}
          onDismiss={handleDismiss}
        />
      )}
    </TooltipGroupContext.Provider>
  )
}

function TooltipGroupBubble({
  id,
  member,
  container,
  fresh,
  onPointerEnter,
  onPointerLeave,
  onDismiss,
}: {
  id: string
  member: TooltipGroupMember
  container: HTMLElement | null
  fresh: boolean
  /** Hovering the bubble itself must keep it open — WCAG 1.4.13 (hoverable). */
  onPointerEnter: () => void
  onPointerLeave: () => void
  onDismiss: () => void
}): React.JSX.Element {
  const floatingRef = useRef<HTMLDivElement | null>(null)
  const arrowRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const floatingEl = floatingRef.current
    if (!floatingEl) return

    const updatePosition = (): void => {
      computePosition(member.element, floatingEl, {
        placement: toPlacement(member.side, member.align),
        strategy: 'fixed',
        middleware: [
          offset(GAP + member.sideOffset + (member.arrow ? ARROW_SIZE / 2 : 0)),
          flip(),
          shift({ padding: 8 }),
          ...(member.arrow && arrowRef.current
            ? [arrowMiddleware({ element: arrowRef.current })]
            : []),
        ],
      })
        .then(({ x, y, placement, middlewareData }): void => {
          floatingEl.style.transform = `translate3d(${x}px, ${y}px, 0)`
          // Reveal only once a real position is known — belt-and-suspenders
          // against ever painting at the CSS default (fixed, top:0, left:0,
          // i.e. the viewport's corner) for even one frame, regardless of
          // how the `fresh` transition-suppression above is computed.
          floatingEl.setAttribute('data-positioned', '')

          const arrowEl = arrowRef.current
          const arrowData = middlewareData.arrow
          if (!arrowEl || !arrowData) return undefined

          const staticSide = STATIC_SIDE_BY_PLACEMENT[placement]

          arrowEl.style.left = arrowData.x != null ? `${arrowData.x}px` : ''
          arrowEl.style.top = arrowData.y != null ? `${arrowData.y}px` : ''
          arrowEl.style.right = ''
          arrowEl.style.bottom = ''
          arrowEl.style[staticSide] = `${-ARROW_SIZE / 2}px`
          return undefined
        })
        .catch(() => {
          // computePosition only rejects if a middleware throws; none of
          // ours do. Nothing meaningful to recover from here.
        })
    }

    return autoUpdate(member.element, floatingEl, updatePosition)
  }, [
    member.element,
    member.side,
    member.align,
    member.sideOffset,
    member.arrow,
  ])

  const bubble = (
    <DismissableLayer
      asChild
      // Focus already moving between our own triggers is not an "outside"
      // interaction — `show`/`scheduleHide` already own that transition. Left
      // enabled, this fires on every Tab (the new target is always outside
      // the portalled bubble) via a stale `onDismiss` closure racing the
      // incoming trigger's own `show()`, which is what closes every other
      // tooltip when tabbing through a group.
      onFocusOutside={(event): void => event.preventDefault()}
      onDismiss={onDismiss}
    >
      <div
        ref={floatingRef}
        id={id}
        role="tooltip"
        className={styles.bubble}
        data-fresh={fresh || undefined}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        {member.content}
        {member.shortcut && <TooltipShortcut shortcut={member.shortcut} />}
        {member.arrow && <div ref={arrowRef} className={styles.arrow} />}
      </div>
    </DismissableLayer>
  )

  return createPortal(bubble, container ?? document.body)
}

export { TooltipGroup, useTooltipGroup }
export type { TooltipGroupProps, TooltipGroupMember, TooltipSide, TooltipAlign }
